/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { SEText } from "../../../../components/SEText";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  DiagnosticoInterface,
  ProjetoInterface,
  RegrasContabilizacaoInterface,
} from "../../../../interfaces";
import { RegrasContabilizacaoService } from "../services";
import useRenderFormStore from "../../stores/useRenderFormStore";
import {
  formatInstrumentString,
  removeEmpty,
} from "../../../../../utils/utils";
import { BaSeDialogContext } from "@base/react";
import useFormMutation from "../../hooks/useFormMutation";
import theme from "../../../../mui-theme";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";
import { TextFieldCounter } from "../../../../components/TextFieldCounter";
import { SEFormUserAction } from "../../../../components/SEFormUserAction";
import { RiCloseCircleLine, RiSearchLine } from "react-icons/ri";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import { BsPlusCircle } from "react-icons/bs";
import { useQuery } from "react-query";
import { parseStringToJson, sortArrayByProp } from "../../../../utils/utils";
import { valueIsRequired } from "../../../../../utils/regex";

const CustomIcon = styled(RiSearchLine)(({ theme }) => ({
  fill: theme.palette.primary.main,
  height: 26,
  width: 26,
  top: "calc(50% - .75em) !important",
}));

interface CPFResult {
  cpf: string;
  nomeRazaoSocial: string;
  codParceiro: string;
  codSebrae: string;
  listaVinculo: { nomeRazaoSocialPJ: string; descCargCli: string }[];
}

const FormularioAgentesSSE = () => {
  const { showModal } = React.useContext(BaSeDialogContext);
  const history = useHistory();
  const location = useLocation();
  const hasCopyParam = location.pathname.includes("/copy");
  const [tema, setTema] = useState("");

  const { data: filterOptions } = useQuery<any>({
    queryFn: () => RegrasContabilizacaoService.getOptions(false),
    queryKey: ["getOptionsRegrasContabilizacao"],
    refetchOnMount: false,
  });

  const { data: projetos } = useQuery<Array<ProjetoInterface>>({
    queryFn: () => RegrasContabilizacaoService.getProjetos(),
    queryKey: ["getProjetosRegrasContabilizacao"],
    refetchOnMount: false,
  });

  const { data: temas, refetch: refetchTemas } = useQuery<
    Array<ProjetoInterface>
  >({
    queryFn: () => RegrasContabilizacaoService.getTemas(tema),
    queryKey: ["getTemasRegrasContabilizacao"],
    refetchOnMount: false,
    enabled: Boolean(tema),
  });

  const { formData: editFormData, handleShowForm } = useRenderFormStore();

  const { control, handleSubmit, reset, setValue, watch, getValues } =
    useForm<RegrasContabilizacaoInterface>({
      defaultValues: {
        nome: "",
        tipo: undefined,
        descricao: "",
        uf: undefined,
        perfil: undefined,
        instrumento: "",
        abordagem: "",
        temaMacro: undefined,
        duracao: undefined,
        unidadeMedida: "",
        periodos: [
          {
            duracao: undefined,
            unidadeMedida: "",
            porte: "TODOS",
          },
        ],
        diagnosticos: [
          {
            diagnostico: {
              id: undefined,
              titulo: "",
            },
            porte: "TODOS",
          },
        ],
        projetos: undefined,
        limiteDias: undefined,
        habilitarSegundaVisita: false,
        usaDiagnostico: false,
      },
    });

  const onResetFields = () => {
    reset({
      nome: "",
      tipo: undefined,
      descricao: "",
      uf: undefined,
      perfil: undefined,
      instrumento: "",
      abordagem: "",
      temaMacro: undefined,
      duracao: undefined,
      unidadeMedida: "",
      periodos: [
        {
          duracao: undefined,
          unidadeMedida: "",
          porte: "TODOS",
        },
      ],
      diagnosticos: [
        {
          diagnostico: {
            id: undefined,
            titulo: "",
          },
          porte: "TODOS",
        },
      ],
      projetos: undefined,
      limiteDias: undefined,
      habilitarSegundaVisita: false,
      usaDiagnostico: false,
    });
  };

  const { append: appendAcoes, remove: removeAcoes } = useFieldArray({
    control,
    name: "acoes",
    rules: {
      required:
        "O registro não pôde ser criado porque ainda há campos que não foram preenchidos.",
    },
  });

  const {
    fields: temasSelecionados,
    append: appendSubTema,
    remove: removeSubTema,
  } = useFieldArray({
    control,
    name: "temas",
    rules: {
      required:
        "O registro não pôde ser criado porque ainda há campos que não foram preenchidos.",
    },
  });

  const {
    fields,
    append: appendPeriodo,
    remove: removePeriodo,
  } = useFieldArray({
    control,
    name: "periodos",
    rules: {
      required:
        "O registro não pôde ser criado porque ainda há campos que não foram preenchidos.",
    },
  });

  const {
    fields: diagnosticosSelecionados,
    append: appendDiagnosticos,
    remove: removeDiagnosticos,
    replace: replaceDiagnosticos,
  } = useFieldArray({
    control,
    name: "diagnosticos",
    rules: {
      required:
        "O registro não pôde ser criado porque ainda há campos que não foram preenchidos.",
    },
  });
  const usaDiagnostico = watch("usaDiagnostico");
  const temaMacro = watch("temaMacro");
  const acoesForm = watch("acoes");

  useEffect(() => {
    if (editFormData && projetos?.length) {
      // Define o projeto selecionado
      editFormData.projetos = projetos.filter(
        (item) => item.id === editFormData?.projetos[0]?.id
      )[0];

      // Reseta o formulário com os dados do registro
      reset(editFormData);

      // Adiciona o subtema se estiver presente no registro
      if (editFormData.tema && !editFormData.temas) {
        appendSubTema(editFormData.tema);
      }
      setValue("usaDiagnostico", !!editFormData?.diagnosticos?.length);

      if (editFormData?.diagnosticos?.length) {
        replaceDiagnosticos(editFormData.diagnosticos);
      } else {
        replaceDiagnosticos([
          {
            id: undefined,
            diagnostico: {
              id: undefined,
              titulo: "",
            },
            porte: "TODOS",
          },
        ]);
      }
    }
    // eslint-disable-next-line
  }, [editFormData, projetos, reset]);

  useEffect(() => {
    if (temaMacro) {
      const temaMacroId =
        typeof temaMacro === "string" ? JSON.parse(temaMacro).id : temaMacro.id;
      setTema(temaMacroId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tema) refetchTemas();
  }, [tema]);

  const goBack = () => {
    handleShowForm(false);
    history.push(`/tabelas-de-controle/regras-contabilizacao`);
  };

  const arrayValues = [
    {
      placeholder: "Padrão",
      value: "TODOS",
    },
    {
      placeholder: "MEI",
      value: "MEI",
    },
    {
      placeholder: "ME",
      value: "ME",
    },
    {
      placeholder: "EPP",
      value: "EPP",
    },
  ];

  const setAcoes = useCallback(
    (data: any) => {
      const dataAcoes = parseStringToJson(data).acoes;
      const inArray = acoesForm?.some((p) =>
        dataAcoes.find((c: any) => c.id === p.id)
      );
      if (!inArray) {
        appendAcoes(dataAcoes);
      }
    },
    [appendAcoes, acoesForm]
  );

  const handleSelectSubtema = (data: any) => {
    if (!data) return;
    const subtema = parseStringToJson(data);
    const isSelected = temasSelecionados?.some(
      (tema) => tema.codigoSiac === subtema.codigoSiac
    );
    if (!isSelected) {
      appendSubTema(subtema);
    }
  };

  const handleNomeAcao = (acao: any) => {
    const projeto = projetos?.find((p) => p?.codigoProjeto === acao?.codPratif);
    return projeto?.nuPratif
      ? `${projeto.nuPratif} - ${projeto.nomePratif} - ${acao.nomeAcao}`
      : acao.nomeAcao;
  };

  const handleRemoveSubtema = (index: number) => {
    removeSubTema(index);
  };

  const handleRemoveDiagnostico = (index: number) => {
    const diagnosticos = watch("diagnosticos");
    if ((diagnosticos?.length ?? 0) > 1) {
      removeDiagnosticos(index);
    }
  };

  const handleAppendDiagnostico = () => {
    const diagnosticos = watch("diagnosticos");
    if ((diagnosticos?.length ?? 0) < 4) {
      appendDiagnosticos({
        // @ts-ignore
        diagnostico: "" as DiagnosticoInterface,
        porte: "",
      });
    }
  };

  const { createMutation, updateMutation } = useFormMutation({
    showModal,
    service: RegrasContabilizacaoService,
    onCloseModalSuccess: () => {
      onResetFields();
      goBack();
    },
    onCloseModalUpdate: () => goBack(),
  });

  const submitForm = async (dataForm: RegrasContabilizacaoInterface) => {
    if (!dataForm.usaDiagnostico || dataForm.usaDiagnostico === "false") {
      delete dataForm.diagnosticos;
      delete dataForm.diagnostico;
    } else {
      dataForm.usaDiagnostico = "yes";
    }

    delete dataForm.tema;
    // @ts-ignore
    dataForm.projetos = new Array(dataForm.projetos);
    // @ts-ignore
    delete dataForm.projetos.acoes;
    // @ts-ignore
    dataForm.acoes = dataForm.acoes.filter((i) => !i.disabled);
    dataForm.limiteDias = Number(dataForm.limiteDias);

    if (hasCopyParam || !dataForm.id) {
      delete dataForm.id;

      await createMutation(removeEmpty(dataForm));
    } else {
      await updateMutation(removeEmpty(dataForm));
    }
  };

  const FormPeriodo = ({ index, checkedValues, isLastItem }: any) => {
    const arrayValuesFormPeriodo = [
      {
        placeholder: "MEI",
        value: "MEI",
      },
      {
        placeholder: "ME",
        value: "ME",
      },
      {
        placeholder: "EPP",
        value: "EPP",
      },
      {
        placeholder: "Padrão",
        value: "TODOS",
      },
    ];

    return (
      <Grid container display="flex" flexDirection="row" gap={2} height={50}>
        <Grid item xs={4}>
          <Controller
            control={control}
            name={`periodos.${index}.duracao`}
            rules={{ required: "Este campo é obrigatório" }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <TextField
                  id={`periodos.${index}.duracao`}
                  label="Duração *"
                  placeholder="Duração"
                  type="number"
                  fullWidth
                  value={value ?? ""}
                  onChange={onChange}
                  inputProps={{ minValue: 1 }}
                />
                {error && (
                  <p style={{ color: "red", marginTop: "8px" }}>
                    {error.message}
                  </p>
                )}
              </>
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <Controller
            control={control}
            name={`periodos.${index}.unidadeMedida`}
            rules={{ required: "Este campo é obrigatório" }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SEFilterSelect
                id={`periodos.${index}.unidadeMedida`}
                label="Unidade de medida *"
                defaultOption={"Selecione uma das opções"}
                IconComponent={CustomIcon}
                value={value}
                onChange={onChange}
                // @ts-ignore
                options={filterOptions?.unidades}
                error={error}
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={3}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          <Controller
            name={`periodos.${index}.porte`}
            control={control}
            rules={{ ...valueIsRequired() }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl component="fieldset" error={Boolean(error)}>
                <RadioGroup
                  row
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                >
                  {arrayValuesFormPeriodo
                    .filter((item) => !checkedValues.includes(item.value))
                    .map((item) => (
                      <FormControlLabel
                        key={item.value}
                        value={item.value}
                        control={<Radio />}
                        label={item.placeholder}
                      />
                    ))}
                </RadioGroup>
                {error && (
                  <p style={{ color: "red", marginTop: "8px" }}>
                    {error.message}
                  </p>
                )}
              </FormControl>
            )}
          />

          <Box display="flex" flexDirection="row" gap="0.5rem">
            <IconButton
              onClick={() => {
                removePeriodo(index);
              }}
              type="button"
              size="small"
            >
              <RiCloseCircleLine size={25} color={"#FF0000"} />
            </IconButton>

            <IconButton
              style={{
                cursor: "pointer",
                display:
                  !isLastItem ||
                  arrayValuesFormPeriodo.filter(
                    (item) => !checkedValues.find((i: any) => i === item.value)
                  ).length <= 1
                    ? "none"
                    : "",
              }}
              onClick={() => {
                appendPeriodo({
                  id: null,
                  duracao: null,
                  unidadeMedida: "",
                  porte: "",
                });
              }}
              type="button"
              size="small"
            >
              <BsPlusCircle size={20} color="#0061C6" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const FormPeriodoTodos = ({ index, checkedValues }: any) => {
    const arrayValuesFormPeriodoTodos = [
      {
        placeholder: "Padrão",
        value: "TODOS",
      },
    ];

    return (
      <Grid container display="flex" flexDirection="row" gap={2}>
        <Grid item xs={4}>
          <Controller
            control={control}
            name={`periodos.${index}.duracao`}
            rules={{ required: "Este campo é obrigatório" }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <TextField
                  id={`periodos.${index}.duracao`}
                  label="Duração *"
                  placeholder="Duração"
                  type="number"
                  fullWidth
                  value={value ?? ""}
                  onChange={onChange}
                  inputProps={{ minValue: 1 }}
                />
                {error && (
                  <p style={{ color: "red", marginTop: "8px" }}>
                    {error.message}
                  </p>
                )}
              </>
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <Controller
            control={control}
            name={`periodos.${index}.unidadeMedida`}
            rules={{ required: "Este campo é obrigatório" }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SEFilterSelect
                id={`periodos.${index}.unidadeMedida`}
                label="Unidade de medida *"
                defaultOption={"Selecione uma das opções"}
                IconComponent={CustomIcon}
                value={value}
                onChange={onChange}
                // @ts-ignore
                options={filterOptions?.unidades}
                error={error}
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={3}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
        >
          <Controller
            name={`periodos.${index}.porte`}
            control={control}
            rules={{ ...valueIsRequired() }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl component="fieldset" error={Boolean(error)}>
                <RadioGroup
                  row
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                >
                  {arrayValuesFormPeriodoTodos
                    .filter((item) => !checkedValues.includes(item.value))
                    .map((item) => (
                      <FormControlLabel
                        key={item.value}
                        value={item.value}
                        control={<Radio />}
                        label={item.placeholder}
                      />
                    ))}
                </RadioGroup>
                {error && (
                  <p style={{ color: "red", marginTop: "8px" }}>
                    {error.message}
                  </p>
                )}
              </FormControl>
            )}
          />
          <IconButton
            style={{
              display: fields.length > 1 ? "none" : "",
            }}
            onClick={() => {
              appendPeriodo({
                id: null,
                duracao: null,
                unidadeMedida: "",
                porte: "",
              });
            }}
            type="button"
            size="small"
          >
            <BsPlusCircle size={20} color="#0061C6" />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
        }}
      >
        <Grid container>
          <Grid container>
            <Grid item xs={12}>
              <SEText
                as="h2"
                size={"medium"}
                font="Mulish"
                weight="bold"
                color="#7B80FF"
              >
                {editFormData?.id
                  ? hasCopyParam
                    ? "Copiar"
                    : "Editar"
                  : "Incluir"}
              </SEText>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} marginTop={2} alignItems={"center"}>
          <Grid item md={4}>
            <Controller
              control={control}
              name="nome"
              rules={{ required: "Este campo é obrigatório" }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <>
                  <TextField
                    id="nome"
                    label="Nome da regra *"
                    placeholder="Nome da regra"
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    inputProps={{ maxLength: 255 }}
                  />
                  <TextFieldCounter value={value} maxLength={255} />
                  {error && (
                    <p style={{ color: "red", marginTop: "8px" }}>
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item md={4} paddingBottom={1.75}>
            <Controller
              control={control}
              name="tipo"
              rules={{ required: "Este campo é obrigatório" }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <SEFilterSelect
                  id="tipo"
                  label="Tipo de registro *"
                  defaultOption={"Selecione uma das opções"}
                  IconComponent={CustomIcon}
                  value={value}
                  displayField="descricao"
                  onChange={onChange}
                  // @ts-ignore
                  options={filterOptions?.tipos}
                  error={error}
                />
              )}
            />
          </Grid>

          <Grid item md={4}>
            <Controller
              control={control}
              name="descricao"
              rules={{ required: "Este campo é obrigatório" }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <>
                  <TextField
                    id="descricao"
                    label="Descrição da realização do registro *"
                    placeholder="Digite o texto que aparecerá na descrição do registro"
                    fullWidth
                    value={value ?? ""}
                    onChange={onChange}
                    inputProps={{ maxLength: 2000 }}
                  />
                  <TextFieldCounter value={value} maxLength={2000} />
                  {error && (
                    <p style={{ color: "red", marginTop: "8px" }}>
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item md={3}>
            <Controller
              control={control}
              name="uf"
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <SEFilterSelect
                  id={"uf"}
                  defaultOption={"Selecione uma das opções"}
                  IconComponent={CustomIcon}
                  label="UF *"
                  value={value}
                  onChange={onChange}
                  options={sortArrayByProp(filterOptions?.ufs, "sigla")}
                  displayField="sigla"
                  error={error}
                />
              )}
            />
          </Grid>

          <Grid item md={3}>
            <Controller
              control={control}
              name="perfil"
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <SEFilterSelect
                  id={"perfil"}
                  defaultOption={"Selecione uma das opções"}
                  IconComponent={CustomIcon}
                  label="Perfil do usuários *"
                  value={value}
                  onChange={onChange}
                  displayField="descricao"
                  options={filterOptions?.perfis}
                  error={error}
                />
              )}
            />
          </Grid>

          <Grid item md={3}>
            <Controller
              control={control}
              name="instrumento"
              rules={{ required: "Este campo é obrigatório" }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <SEFilterSelect
                  id="instrumento"
                  label="Instrumento *"
                  customLabel={formatInstrumentString}
                  defaultOption={"Selecione uma das opções"}
                  IconComponent={CustomIcon}
                  value={value}
                  onChange={onChange}
                  // @ts-ignore
                  options={filterOptions?.instrumentos}
                  error={error}
                />
              )}
            />
          </Grid>

          <Grid item md={3}>
            <Controller
              control={control}
              name="abordagem"
              rules={{ required: "Este campo é obrigatório" }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <SEFilterSelect
                  id="abordagem"
                  label="Abordagem *"
                  defaultOption={"Selecione uma das opções"}
                  IconComponent={CustomIcon}
                  value={value}
                  onChange={onChange}
                  // @ts-ignore
                  options={filterOptions?.abordagens}
                  error={error}
                />
              )}
            />
          </Grid>

          <Grid
            container
            spacing={2}
            marginTop={2}
            paddingX={2}
            alignItems={"center"}
          >
            <Grid item md={4}>
              <Controller
                control={control}
                name="temaMacro"
                rules={{ required: "Este campo é obrigatório" }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <SEFilterSelect
                    id="temaMacro"
                    label="Tema *"
                    displayField="descricao"
                    defaultOption={"Selecione uma das opções"}
                    IconComponent={CustomIcon}
                    value={value}
                    onChange={(e) => {
                      setTema(e.id);
                      onChange(e);
                    }}
                    // @ts-ignore
                    options={filterOptions?.temasMacro}
                    error={error}
                  />
                )}
              />
            </Grid>

            {temas?.length ? (
              <>
                <Grid item md={4}>
                  <Controller
                    control={control}
                    name="tema"
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <SEFilterSelect
                        id="tema"
                        label="Subtema *"
                        displayField="descricao"
                        defaultOption={"Selecione uma das opções"}
                        IconComponent={CustomIcon}
                        value={value}
                        onChange={onChange}
                        // @ts-ignore
                        options={temas}
                        error={error}
                      />
                    )}
                  />
                </Grid>
                <Grid item width={"5%"} display={"flex"} justifyContent={"end"}>
                  <IconButton
                    onClick={() => {
                      const subtemaSelecionado = getValues("tema");
                      handleSelectSubtema(subtemaSelecionado);
                    }}
                    type="button"
                  >
                    <BsPlusCircle size={20} color="#0061C6" />
                  </IconButton>
                </Grid>
                {temasSelecionados?.map((item, index) => {
                  return (
                    <Grid item key={item.id}>
                      <Box
                        key={index}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            backgroundColor: "#9999FF1A",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #E1E6EF",
                            width: "100%",
                          }}
                        >
                          <SEText
                            as="h2"
                            size={"medium"}
                            font="Mulish"
                            weight="bold"
                          >
                            {item?.descricao}
                          </SEText>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ marginLeft: 2, borderColor: "#E0E0E0" }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveSubtema(index)}
                          >
                            <RiCloseCircleLine size={20} color={"#FF0000"} />
                          </IconButton>
                        </Button>
                      </Box>
                    </Grid>
                  );
                })}
              </>
            ) : (
              <></>
            )}
          </Grid>

          <Grid
            container
            spacing={2}
            marginTop={2}
            paddingX={2}
            flexDirection={"column"}
          >
            {fields.map((item, index) => {
              return (
                <Grid item key={item.id}>
                  {item.porte === "TODOS" ? (
                    <FormPeriodoTodos
                      index={index}
                      checkedValues={fields
                        .filter((i) => i.id !== item.id)
                        .map((i) => i.porte)}
                      item={item}
                      isLastItem={index === fields.length - 1}
                    />
                  ) : (
                    <FormPeriodo
                      index={index}
                      checkedValues={fields
                        .filter((i) => i.id !== item.id)
                        .map((i) => i.porte)}
                      item={item}
                      isLastItem={index === fields.length - 1}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>

          <Grid container spacing={2} marginTop={2} paddingX={2}>
            <Grid item xs={12}>
              <Grid container display="flex" flexDirection="row" gap={2}>
                <Grid item md={4} display="flex" flexDirection="row">
                  <Grid item md={6}>
                    <Controller
                      name="publicos"
                      control={control}
                      rules={{ ...valueIsRequired() }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl
                          component="fieldset"
                          error={Boolean(error)}
                        >
                          <FormLabel component="legend" sx={{ fontSize: 16 }}>
                            Público-alvo *
                          </FormLabel>
                          <FormGroup row>
                            {filterOptions?.publicos?.map((item: any) => (
                              <FormControlLabel
                                key={item.id}
                                control={
                                  <Checkbox
                                    checked={
                                      field.value?.some(
                                        (selected: any) =>
                                          selected.id === item.id
                                      ) || false
                                    }
                                    onChange={(e) => {
                                      const value = [...(field.value || [])];
                                      if (e.target.checked) {
                                        value.push(item);
                                      } else {
                                        const index = value.findIndex(
                                          (selected: any) =>
                                            selected.id === item.id
                                        );
                                        if (index > -1) {
                                          value.splice(index, 1);
                                        }
                                      }
                                      field.onChange(value);
                                    }}
                                  />
                                }
                                label={item.nome}
                              />
                            ))}
                          </FormGroup>
                          {error && (
                            <p style={{ color: "red", marginTop: "8px" }}>
                              {error.message}
                            </p>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <Controller
                      name="habilitarSegundaVisita"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl
                          component="fieldset"
                          error={Boolean(error)}
                        >
                          <FormLabel component="legend" sx={{ fontSize: 16 }}>
                            Realiza 2 visitas? *
                          </FormLabel>
                          <RadioGroup row {...field}>
                            <FormControlLabel
                              value={false}
                              control={<Radio />}
                              label="Não"
                            />
                            <FormControlLabel
                              value={true}
                              control={<Radio />}
                              label="Sim"
                            />
                          </RadioGroup>
                          {error && (
                            <p style={{ color: "red", marginTop: "8px" }}>
                              {error.message}
                            </p>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid item md={4}>
                  <Controller
                    control={control}
                    name="limiteDias"
                    render={({
                      field: { value, onChange },
                      fieldState: { error },
                    }) => (
                      <>
                        <TextField
                          id="limiteDias"
                          type="number"
                          label="Limite de atendimentos por dia (unidades)"
                          placeholder="Digite o texto que aparecerá na descrição do registro"
                          fullWidth
                          value={value ?? ""}
                          onChange={onChange}
                        />
                        {error && (
                          <p style={{ color: "red", marginTop: "8px" }}>
                            {error.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container alignItems="center" paddingLeft={2} paddingTop={2}>
            <Grid item xs={4} alignItems="center">
              <Controller
                name="usaDiagnostico"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormControl component="fieldset" error={Boolean(error)}>
                    <FormLabel component="legend" sx={{ fontSize: 16 }}>
                      Vai usar um diagnóstico? *
                    </FormLabel>
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Sim"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="Não"
                      />
                    </RadioGroup>
                    {error && (
                      <p style={{ color: "red", marginTop: "8px" }}>
                        {error.message}
                      </p>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>

          <Grid
            container
            display={"flex"}
            alignItems="center"
            paddingLeft={2}
            paddingTop={2}
          >
            {Boolean(usaDiagnostico)
              ? diagnosticosSelecionados.map((item, index) => {
                  const previousItem = getValues(`diagnosticos.${index - 1}`);
                  const selectedValues = diagnosticosSelecionados
                    .filter((i) => i.id !== item.id)
                    .map((i) => i.porte);
                  const isSelectedPreviously = selectedValues.includes(
                    previousItem?.porte
                  );
                  const availableOptions =
                    index === 0
                      ? arrayValues.filter((option) => option.value === "TODOS")
                      : arrayValues.filter(
                          (option) =>
                            !isSelectedPreviously ||
                            !selectedValues.includes(option.value)
                        );
                  return (
                    <Grid
                      container
                      direction="row"
                      gap="0.5rem"
                      key={item.id}
                      marginTop={!index ? 0 : 2}
                    >
                      <Grid item xs={4}>
                        <Controller
                          control={control}
                          name={`diagnosticos.${index}.diagnostico`}
                          rules={{ required: "Este campo é obrigatório" }}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <SEFilterSelect
                              id={`diagnosticos.${index}.diagnostico`}
                              label="Qual diagnóstico? *"
                              defaultOption={"Selecione uma das opções"}
                              IconComponent={CustomIcon}
                              value={value}
                              onChange={onChange}
                              displayField="titulo"
                              // @ts-ignore
                              options={filterOptions?.diagnosticos}
                              error={error}
                            />
                          )}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={4}
                        display={"flex"}
                        flexDirection="row"
                        gap="1rem"
                        alignItems="center"
                        justifyContent="flex-start"
                      >
                        <Controller
                          name={`diagnosticos.${index}.porte`}
                          control={control}
                          rules={{ ...valueIsRequired() }}
                          render={({ field, fieldState: { error } }) => (
                            <FormControl
                              component="fieldset"
                              error={Boolean(error)}
                            >
                              <RadioGroup row {...field}>
                                {availableOptions.map((option, idx) => (
                                  <FormControlLabel
                                    key={idx}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.placeholder}
                                  />
                                ))}
                              </RadioGroup>
                              {error && (
                                <p style={{ color: "red", marginTop: "8px" }}>
                                  {error.message}
                                </p>
                              )}
                            </FormControl>
                          )}
                        />
                        <Box display="flex" flexDirection="row" gap="0.5rem">
                          <IconButton
                            onClick={() => {
                              handleRemoveDiagnostico(index);
                            }}
                            style={{
                              display: !index ? "none" : "",
                            }}
                            type="button"
                            size="small"
                          >
                            <RiCloseCircleLine size={25} color={"#FF0000"} />
                          </IconButton>

                          <IconButton
                            style={{
                              cursor: "pointer",
                              display: !(index === fields.length - 1)
                                ? "none"
                                : "",
                            }}
                            onClick={() => {
                              handleAppendDiagnostico();
                            }}
                            type="button"
                            size="small"
                          >
                            <BsPlusCircle size={20} color="#0061C6" />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  );
                })
              : null}
          </Grid>

          <Grid item md={3}>
            <Controller
              control={control}
              name="projetos"
              rules={{ required: "Este campo é obrigatório" }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <>
                  <SEFilterSelect
                    id="projetos"
                    defaultOption={"Selecione uma das opções"}
                    label="Projeto / Ação *"
                    placeholder="Projeto / Ação"
                    IconComponent={CustomIcon}
                    value={value}
                    onChange={onChange}
                    options={projetos ?? []}
                    displayField="nomePratif"
                    error={error}
                  />
                  {error && (
                    <p style={{ color: "red", marginTop: "8px" }}>
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item md={1}>
            <IconButton
              onClick={() => {
                const p = watch("projetos");
                if (p) setAcoes(p);
              }}
              type="button"
            >
              <BsPlusCircle size={20} />
            </IconButton>
          </Grid>
          {Boolean(acoesForm?.length) && (
            <Grid container spacing={2} marginTop={2} marginLeft={1}>
              <Box
                display="flex"
                flexDirection={"column"}
                gap="0.5rem"
                flexWrap="wrap"
                mt={3}
                width="80%"
              >
                {acoesForm?.map((field, index) => {
                  return (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          backgroundColor: "#9999FF1A",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #E1E6EF",
                          width: "100%",
                        }}
                      >
                        <SEText
                          as="h2"
                          size={"medium"}
                          font="Mulish"
                          weight="bold"
                        >
                          {handleNomeAcao(field)}
                        </SEText>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ marginLeft: 2, borderColor: "#E0E0E0" }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => removeAcoes(index)}
                        >
                          <RiCloseCircleLine size={20} color={"#FF0000"} />
                        </IconButton>
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          )}

          <Grid
            item
            md={12}
            display="flex"
            justifyContent="flex-end"
            gap={2}
            marginTop={2}
          >
            <Button variant="outlined" onClick={goBack}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              type="submit"
              onClick={handleSubmit(submitForm)}
              disabled={false}
            >
              {editFormData?.id
                ? hasCopyParam
                  ? "Copiar"
                  : "Editar"
                : "Incluir"}
            </Button>
          </Grid>

          <Grid item md={12} marginTop={3}>
            <SEFormUserAction data={editFormData} />
          </Grid>
        </Grid>
      </Paper>
    </ThemeProvider>
  );
};

export default FormularioAgentesSSE;
